// Hit-testing (DRAW_PLAN §5): R-tree candidates, then exact tests on a scratch canvas: fill (winding rule)
// and stroke (half the stroke width plus a tolerance in screen pixels).
import type { DisplayList, Item } from '../render/displayList';
import type { Rect } from '../model/types';

let scratch: OffscreenCanvasRenderingContext2D | null = null;
const ctx = () => (scratch ??= new OffscreenCanvas(1, 1).getContext('2d')!);

/** Topmost visible, unlocked item at world (x, y); `tol` is the tolerance in world units. */
export function hitItem(list: DisplayList, x: number, y: number, tol: number): Item | null {
  const cands = list.tree.search({ minX: x - tol, minY: y - tol, maxX: x + tol, maxY: y + tol });
  cands.sort((a, b) => b.order - a.order);
  const g = ctx();
  for (const it of cands) {
    if (it.hidden || it.locked || !it.path) continue;
    g.setTransform(...it.world);
    const n = it.node;
    // M14: text is hit anywhere in its box, an image anywhere in its frame.
    if (n.kind === 'text') {
      if (
        x >= it.gbounds.x - tol &&
        x <= it.gbounds.x + it.gbounds.w + tol &&
        y >= it.gbounds.y - tol &&
        y <= it.gbounds.y + it.gbounds.h + tol
      )
        return it;
      continue;
    }
    if (n.kind === 'image') {
      if (g.isPointInPath(it.path, x, y)) return it;
      continue;
    }
    const hasFill = n.appearance.fills.some((f) => f.paint.kind !== 'none');
    const rule: CanvasFillRule = n.kind === 'path' ? n.fillRule : 'nonzero';
    if (hasFill && g.isPointInPath(it.path, x, y, rule)) return it;
    const scaleW = Math.sqrt(Math.abs(it.world[0] * it.world[3] - it.world[1] * it.world[2])) || 1;
    const maxW = Math.max(0, ...n.appearance.strokes.map((s) => s.width));
    g.lineWidth = maxW + (2 * tol) / scaleW;
    if (g.isPointInStroke(it.path, x, y)) return it;
  }
  return null;
}

/** Items whose bounds lie fully inside (or, with `touch`, touch) a world rect. */
export function itemsInRect(list: DisplayList, r: Rect, touch = true): Item[] {
  return list.tree
    .search({ minX: r.x, minY: r.y, maxX: r.x + r.w, maxY: r.y + r.h })
    .filter(
      (it) =>
        !it.hidden &&
        !it.locked &&
        (touch || (it.minX >= r.x && it.maxX <= r.x + r.w && it.minY >= r.y && it.maxY <= r.y + r.h)),
    );
}
